import type { UploadResult } from '@/services/fileService';

import type { UploadItem, UploadStatus } from './types';

// ---------------------------------------------------------------------------
// State & Actions
// ---------------------------------------------------------------------------

export interface BatchState {
  items: UploadItem[];
  isPanelVisible: boolean;
  isPanelMinimized: boolean;
}

export type Action =
  | { type: 'ADD_FILES'; items: UploadItem[] }
  | { type: 'START_HASH'; id: string }
  | { type: 'START_UPLOAD'; id: string }
  | { type: 'UPDATE_PROGRESS'; id: string; progress: number }
  | { type: 'COMPLETE'; id: string; result: UploadResult }
  | { type: 'MARK_DUPLICATE'; id: string; result: UploadResult }
  | { type: 'FAIL'; id: string; error: string }
  | { type: 'CANCEL_ITEM'; id: string }
  | { type: 'CANCEL_ALL' }
  | { type: 'RETRY_ITEM'; id: string; abortController: AbortController }
  | { type: 'REMOVE_ITEM'; id: string }
  | { type: 'CLEAR_COMPLETED' }
  | { type: 'TOGGLE_MINIMIZE' }
  | { type: 'HIDE_PANEL' }
  | { type: 'SHOW_PANEL' };

export const TERMINAL_STATUSES: Set<UploadStatus> = new Set([
  'completed',
  'failed',
  'cancelled',
  'duplicate',
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function updateItem(
  items: UploadItem[],
  id: string,
  patch: Partial<UploadItem>,
): UploadItem[] {
  return items.map((item) => (item.id === id ? { ...item, ...patch } : item));
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function batchReducer(state: BatchState, action: Action): BatchState {
  switch (action.type) {
    case 'ADD_FILES':
      return {
        ...state,
        items: [...state.items, ...action.items],
        isPanelVisible: true,
      };

    case 'START_HASH':
      return { ...state, items: updateItem(state.items, action.id, { status: 'hashing' }) };

    case 'START_UPLOAD':
      return {
        ...state,
        items: updateItem(state.items, action.id, { status: 'uploading', progress: 0 }),
      };

    case 'UPDATE_PROGRESS':
      return {
        ...state,
        items: updateItem(state.items, action.id, { progress: action.progress }),
      };

    case 'COMPLETE':
      return {
        ...state,
        items: updateItem(state.items, action.id, {
          status: 'completed',
          progress: 100,
          result: action.result,
        }),
      };

    case 'MARK_DUPLICATE':
      return {
        ...state,
        items: updateItem(state.items, action.id, {
          status: 'duplicate',
          result: action.result,
        }),
      };

    case 'FAIL':
      return {
        ...state,
        items: updateItem(state.items, action.id, {
          status: 'failed',
          error: action.error,
          progress: 0,
        }),
      };

    case 'CANCEL_ITEM':
      return {
        ...state,
        items: updateItem(state.items, action.id, { status: 'cancelled', progress: 0 }),
      };

    case 'CANCEL_ALL': {
      const updated = state.items.map((item) => {
        if (TERMINAL_STATUSES.has(item.status)) return item;
        return { ...item, status: 'cancelled' as const, progress: 0 };
      });
      return { ...state, items: updated };
    }

    case 'RETRY_ITEM':
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.id
            ? {
                ...item,
                status: 'queued' as const,
                progress: 0,
                error: null,
                result: null,
                abortController: action.abortController,
              }
            : item,
        ),
      };

    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter((i) => i.id !== action.id) };

    case 'CLEAR_COMPLETED':
      return {
        ...state,
        items: state.items.filter((i) => !TERMINAL_STATUSES.has(i.status)),
      };

    case 'TOGGLE_MINIMIZE':
      return { ...state, isPanelMinimized: !state.isPanelMinimized };

    case 'HIDE_PANEL':
      return { ...state, isPanelVisible: false, isPanelMinimized: false };

    case 'SHOW_PANEL':
      return { ...state, isPanelVisible: true };

    default:
      return state;
  }
}

export const INITIAL_STATE: BatchState = {
  items: [],
  isPanelVisible: false,
  isPanelMinimized: false,
};
